import { motion, AnimatePresence } from 'motion/react';
import { Fingerprint, X } from 'lucide-react';
import { lightImpact } from '@/utils/haptics';

interface BiometricPromptProps {
  isOpen: boolean;
  onEnable: () => void;
  onSkip: () => void;
  isAvailable: boolean;
}

const BiometricPrompt = ({ isOpen, onEnable, onSkip, isAvailable }: BiometricPromptProps) => {
  return (
    <AnimatePresence>
      {isOpen && isAvailable && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm sm:items-center"
          onClick={onSkip}
        >
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl"
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                Enable Quick Login
              </h3>
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={async () => { await lightImpact(); onSkip(); }}
                className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors min-h-12 min-w-12 flex items-center justify-center"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-slate-500" />
              </motion.button>
            </div>

            <div className="flex flex-col items-center mb-8">
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
                className="w-20 h-20 rounded-full gradient-bg flex items-center justify-center mb-4 shadow-glow"
              >
                <Fingerprint className="w-10 h-10 text-white" />
              </motion.div>
              <p className="text-center text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                Use Face ID or fingerprint for faster, more secure access to Sheehan AI.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={async () => { await lightImpact(); onEnable(); }}
                className="w-full py-4 rounded-2xl gradient-bg text-white font-semibold text-base shadow-glow min-h-12"
              >
                Enable Biometric
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={async () => { await lightImpact(); onSkip(); }}
                className="w-full py-4 rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium text-base
                           active:bg-slate-200 dark:active:bg-slate-700 transition-colors min-h-12"
              >
                Skip for Now
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BiometricPrompt;
